<script setup>
import { computed } from 'vue'
import { Icon } from '@iconify/vue'
import AppButton from '@/components/atoms/AppButton'
import emptyBoxImg from '@/assets/3d-assets/icons/empty-box.png'

/**
 * AppEmptyState — reusable empty-state card.
 *
 *   ┌──────────────────────────────────────┐
 *   │                                  [X] │  ← optional dismiss
 *   │                                      │
 *   │              [🗃️ illustration]       │
 *   │                                      │
 *   │            Title goes here           │
 *   │          Description goes here       │
 *   │                                      │
 *   │      ┌────────────────────────┐      │
 *   │      │      Action label      │      │
 *   │      └────────────────────────┘      │
 *   └──────────────────────────────────────┘
 *
 * Matches Figma node 23996:571601: white card, `border border-blue-200`,
 * blue glow shadow, p-4, gap-6 between the illustration block and the
 * full-width action button. Use anywhere a list / table / section has
 * nothing to show. For purely decorative tint-circle empty states (legacy
 * `EmptyInventoryState`, `EmptyBatchState`, `EmptyListingsState`), set
 * `variant="plain"` and provide an image via the `#image` slot.
 *
 * Built from existing primitives:
 *   • AppButton (action)
 *   • @iconify/vue Icon (dismiss)
 * No store / router imports — caller drives @action / @dismiss.
 */
const props = defineProps({
  /** Image src for the illustration. Defaults to the 3D empty-box PNG; `null` draws none. */
  imageSrc:    { type: String, default: emptyBoxImg },
  imageAlt:    { type: String, default: '' },

  title:       { type: String, default: '' },
  description: { type: String, default: '' },

  /** Renders the default action button when set. Hide by passing an empty
   *  string AND providing a custom `#action` slot, or by omitting both. */
  actionLabel: { type: String, default: '' },
  /** AppButton variant for the default action. */
  actionVariant: { type: String, default: 'primary' },
  /** Iconify name for a leading icon on the default action (`+ Create a tag`). */
  actionIcon:  { type: String, default: null },

  /** Shows the top-right X dismiss button. Emits `@dismiss`. */
  dismissible: { type: Boolean, default: false },

  /**
   * Chrome variant.
   *   • `card`  — white card + blue-200 border + blue glow (Figma 23996:571601, default)
   *   • `plain` — transparent wrapper, no border / shadow / padding chrome.
   *               Use when the caller already supplies its own surround
   *               (e.g. inside a Dialog) or wants the legacy tint-circle look.
   */
  variant:     { type: String, default: 'card' }, // 'card' | 'plain'

  /**
   * Max width of the card. Tailwind class — default `max-w-[626px]` matches
   * the Figma frame width. Pass `'max-w-full'` to fill the parent.
   */
  maxWidth:    { type: String, default: 'max-w-[626px]' },

  /**
   * Typographic scale.
   *   • `md` — the Figma page-level empty state (2xl bold title, full-width
   *            action). Default; nothing changes for existing consumers.
   *   • `sm` — compact, for an empty state nested INSIDE a card/panel/section
   *            that already has its own heading (settings rows, detail-panel
   *            sections). Smaller illustration, base-weight title, tighter
   *            stack, and an auto-width action instead of a full-width bar —
   *            a 626px-wide primary button inside a settings row reads as the
   *            section's main call to action, which it isn't.
   */
  size:        { type: String, default: 'md', validator: (v) => ['md', 'sm'].includes(v) },
})

const emit = defineEmits(['action', 'dismiss'])

const isCard = computed(() => props.variant === 'card')
const isSmall = computed(() => props.size === 'sm')

const wrapperClass = computed(() =>
  isCard.value
    ? [
        'relative',
        'bg-white border border-blue-200 rounded-lg p-4 w-full mx-auto',
        // Figma blue glow: `drop-shadow-[0px_0px_2.5px_#69a0ff]`. Translates
        // to a faint tint-coloured shadow — the card reads as "active focal
        // element", which is the intent in the design.
        'shadow-[0_0_2.5px_0_rgba(105,160,255,0.6)]',
        props.maxWidth,
      ]
    : ['relative w-full', props.maxWidth]
)

// One place per scale, so a future size can't half-apply.
const scale = computed(() => (isSmall.value
  ? {
      stack: 'gap-3',
      block: 'gap-2',
      copy:  'gap-1',
      image: 'w-[52px] h-[32px] object-contain select-none',
      title: 'text-base font-semibold text-gray-900',
      description: 'text-xs text-gray-500 leading-[1.5]',
      action: 'w-auto',
      button: 'sm',
    }
  : {
      stack: 'gap-6',
      block: 'gap-4',
      copy:  'gap-2',
      image: 'w-[95px] h-[58px] object-contain select-none',
      title: 'text-2xl font-bold text-gray-900 leading-[1.4] tracking-[-0.01em]',
      description: 'text-sm text-gray-700 leading-[1.5]',
      action: 'w-full',
      button: 'md',
    }))
</script>

<template>
  <div :class="wrapperClass">
    <!-- Top-right dismiss — absolutely positioned so the centered content
         stack below can't overlap its hit-area. Earlier this lived in a
         flow row with `-mb-4`, and the next stack's `w-full` container
         visually sat above the X (clicks went through to the empty side
         space, not the button). `z-10` keeps it above any slot content. -->
    <button
      v-if="dismissible"
      type="button"
      aria-label="Dismiss"
      class="absolute top-4 right-4 z-10 w-6 h-6 inline-flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors"
      @click="emit('dismiss')"
    >
      <Icon icon="heroicons:x-mark" class="w-5 h-5" />
    </button>

    <div class="flex flex-col items-center" :class="scale.stack">
      <!-- Illustration + copy block -->
      <div class="flex flex-col items-center w-full" :class="scale.block">
        <slot name="image">
          <img
            v-if="imageSrc"
            :src="imageSrc"
            :alt="imageAlt"
            :class="scale.image"
            draggable="false"
          />
        </slot>

        <div class="flex flex-col items-center text-center w-full break-words" :class="scale.copy">
          <slot name="title">
            <p v-if="title" :class="scale.title">
              {{ title }}
            </p>
          </slot>
          <slot name="description">
            <p v-if="description" :class="scale.description">
              {{ description }}
            </p>
          </slot>
          <!-- Extra content beneath the description (default slot). Used by
               the success variant for the TOTAL PICKED / TOTAL ORDERS summary
               cards (Figma 23996:571433). Stays in the same vertical stack
               as title + description with the same gap-2, matching the
               Figma "Slot 2" group. -->
          <slot />
        </div>
      </div>

      <!-- Action — full-width primary button by default. Hidden when no
           label and no slot content. -->
      <div v-if="$slots.action || actionLabel" :class="scale.action">
        <slot name="action">
          <AppButton
            :variant="actionVariant"
            :size="scale.button"
            :leading-icon="actionIcon"
            :class="scale.action"
            @click="emit('action')"
          >
            {{ actionLabel }}
          </AppButton>
        </slot>
      </div>
    </div>
  </div>
</template>
